/**
 * IncidentsBowler – SRE Page 0 style horizontal bowler chart for L0 incidents.
 * Columns = periods (weekly / monthly / quarterly) for the selected time range.
 * Tabs    = one per source (fetched from /incidents/sources).
 * Each metric row shows Target + Actual sub-rows with green/red colouring.
 */
import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Refresh from '@mui/icons-material/Refresh';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import EditOutlined from '@mui/icons-material/EditOutlined';
import History from '@mui/icons-material/History';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import Snackbar from '@mui/material/Snackbar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import { API_BASE_URL } from '../apiClient';
import ALLOWED_BOWLER_USERS from '../config/allowed_bowler_users';
import BOWLER_EDIT_USERS from '../config/bowler_edit_users';
import BOWLER_ADMIN_USERS from '../config/bowler_admin_users';
import { useMsal } from '@azure/msal-react';
import { authFetch } from '../services/authFetch';

// ── Types ────────────────────────────────────────────────────────────────────

type PeriodType = 'week' | 'month';
type TimeRange  = '4w'   | '13w'  | '26w'    | 'ytd';

interface SeriesKPI {
  period_start: string;
  incs_created:  number | null;
  incs_resolved: number | null;
  avg_e2e_mttr:  number | null;
  p60_e2e_mttr:  number | null;
  p90_e2e_mttr:  number | null;
  avg_frt:       number | null;
  automation_pct:             number | null;
  automation_mttr:            number | null;
  reopen_pct:                 number | null;
  update_framework_compliance:number | null;
  ftf_pct:                    number | null;
  inc_resolution_pct:         number | null;
  avg_support_mttr:           number | null;
  avg_mttr_diff:              number | null;
  // not yet calculated — placeholders for future tracking
  new_issue?:       number | null;
  problem_tickets?: number | null;
  tori_fux?:        number | null;
  // manually tracked P90 breakdowns
  p90_e2e_mttr_manual?:      number | null;
  p90_e2e_mttr_automation?:  number | null;
}

interface ByPeriod {
  by_source:           Record<string, SeriesKPI[]>;
  by_assignment_group: Record<string, SeriesKPI[]>;
}
interface BulkData {
  weekly:          ByPeriod;
  monthly:         ByPeriod;
  group_to_source: Record<string, string>;
}

interface MonthGroup {
  monthKey:   string;                              // e.g. "2026-04"
  monthLabel: string;                              // e.g. "Apr"
  periods:    Array<{ key: string; label: string }>;
}

interface AuditEntry {
  id:            number;
  group_category: string;
  period_start:  string;
  metric_key:    string;
  override_type: 'target' | 'actual';
  old_value:     number | null;
  new_value:     number | null;
  updated_by:    string;
  updated_at:    string;
}

interface EditingCell {
  metricKey:   string;
  periodStart: string;  // ISO date for actuals; 'target' sentinel for target overrides
  type:        'target' | 'actual';
  inputVal:    string;
  originalVal: number | null;  // value when editing started — used to skip no-op saves
}

// ── Bowler metric definitions ─────────────────────────────────────────────────

interface BowlerMetric {
  group:          string;
  label:          string;
  unit:           string;
  baseline:       number | string | null;
  target:         number | null;
  direction:      'up' | 'down';
  key:            keyof SeriesKPI;
  notCalculated?: boolean;   // true = not yet tracked; render grey
  manual?: boolean;          // true = manually entered/tracked (vs auto-calculated)
}

const METRICS: BowlerMetric[] = [
  // Volume Inflow
  { group: 'Volume Inflow',                         label: 'INCs Raised',                              unit: 'Count', baseline: 180, target: 180, direction: 'down', key: 'incs_created' },
  // Resolution Rate
  { group: 'Resolution Rate',                       label: 'INC Resolution',                           unit: '%',     baseline: 44,  target: 52,  direction: 'up',   key: 'inc_resolution_pct' },
  // Resolution Speed
  { group: 'Resolution Speed',                      label: 'INC E2E',                                  unit: 'Hours', baseline: 150, target: 150, direction: 'down', key: 'avg_e2e_mttr' },
  { group: 'Resolution Speed',                      label: 'INC E2E - P90',                            unit: 'Hours', baseline: 333, target: 333, direction: 'down', key: 'p90_e2e_mttr' },
  { group: 'Resolution Speed',                      label: 'INC E2E - P90 (Manual)',                   unit: 'Hours', baseline: 148, target: 148, direction: 'down', key: 'p90_e2e_mttr_manual' },
  { group: 'Resolution Speed',                      label: 'INC E2E - P90 (Automation)',               unit: 'Hours', baseline: 72,  target: 72,  direction: 'down', key: 'p90_e2e_mttr_automation' },
  { group: 'Resolution Speed',                      label: 'INC E2E - P60',                            unit: 'Hours', baseline: 100, target: 100, direction: 'down', key: 'p60_e2e_mttr' },
  { group: 'Resolution Speed',                      label: 'FRT',                                      unit: 'Hours', baseline: 24,  target: 24,  direction: 'down', key: 'avg_frt' },
  // Automation Fulfillment
  { group: 'Automation Fulfillment',                label: 'INCs Automation',                          unit: '%',     baseline: 0,   target: 0,   direction: 'up',   key: 'automation_pct' },
  { group: 'Automation Fulfillment',                label: 'TORI First User Experience (BU)',           unit: '%',     baseline: 0,   target: null, direction: 'up',  key: 'tori_fux', notCalculated: true, manual: true },
  { group: 'Automation Fulfillment',                label: 'INC MTTR - Automation Tech Support',       unit: 'Hours', baseline: 0,   target: null, direction: 'down', key: 'automation_mttr' },
  // Volume Reduction
  { group: 'Volume Reduction',                      label: 'New Issue Type(s) + Volume',               unit: 'Count', baseline: 1,   target: 1,   direction: 'down', key: 'new_issue',       notCalculated: true, manual: true },
  { group: 'Volume Reduction',                      label: 'Problem Tickets Created',                  unit: '#',     baseline: 4,   target: 4,   direction: 'up',   key: 'problem_tickets', notCalculated: true, manual: true },
  // Ticket handling Quality Fulfillment
  { group: 'Ticket handling Quality Fulfillment',   label: 'INCs FTF',                                 unit: '%',     baseline: 40,  target: 40,  direction: 'up',   key: 'ftf_pct' },
  { group: 'Ticket handling Quality Fulfillment',   label: 'Reopen Rate %',                            unit: '%',     baseline: 5,   target: 5,   direction: 'down', key: 'reopen_pct' },
  { group: 'Ticket handling Quality Fulfillment',   label: 'Update framework compliance',              unit: '%',     baseline: 90,  target: 90,  direction: 'up',   key: 'update_framework_compliance' },
  { group: 'Ticket handling Quality Fulfillment',   label: 'INCs Resolved - Tech Support',             unit: 'Count', baseline: 44,  target: 44,  direction: 'up',   key: 'incs_resolved' },
  { group: 'Ticket handling Quality Fulfillment',   label: 'Difference between Support MTTR and E2E MTTR', unit: 'Hours', baseline: 150, target: 150, direction: 'down', key: 'avg_mttr_diff' },
];

// Keys for the 8 INC metrics that are visible by default (in desired display order)
const DEFAULT_METRIC_KEYS: ReadonlySet<string> = new Set([
  'incs_created', 'inc_resolution_pct',
  'avg_e2e_mttr', 'p90_e2e_mttr', 'p90_e2e_mttr_manual', 'p90_e2e_mttr_automation',
  'automation_pct', 'ftf_pct',
]);

const GROUP_ORDER = ['Volume Inflow', 'Resolution Rate', 'Resolution Speed', 'Automation Fulfillment', 'Volume Reduction', 'Ticket handling Quality Fulfillment'];

interface GroupEntry { metrics: BowlerMetric[]; rowSpan: number; }
const GROUPS: Map<string, GroupEntry> = new Map(
  GROUP_ORDER.map(g => {
    const ms = METRICS.filter(m => m.group === g);
    return [g, { metrics: ms, rowSpan: ms.length * 2 }]; // 2 rows (Target + Actual) per metric
  })
);

// ── Helpers ───────────────────────────────────────────────────────────────────

const getDateRange = (tr: TimeRange): { start: string; end: string } => {
  const end   = new Date();
  const start = new Date(end);
  if      (tr === '4w')  start.setDate(end.getDate() - 28);
  else if (tr === '13w') start.setDate(end.getDate() - 91);
  else if (tr === '26w') start.setDate(end.getDate() - 182);
  else { start.setUTCMonth(0); start.setUTCDate(1); }
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
};

// Floor a YYYY-MM-DD to the Monday of its ISO week (so Wk1 is never cut off)
const floorToMonday = (iso: string): string => {
  const d = new Date(iso + 'T00:00:00Z');
  const dow = (d.getUTCDay() + 6) % 7; // Mon=0 … Sun=6
  d.setUTCDate(d.getUTCDate() - dow);
  return d.toISOString().slice(0, 10);
};

// Returns ISO week number, ISO year and a display label for a YYYY-MM-DD date string
const getISOWeekInfo = (isoDate: string): { label: string; isoYear: number; week: number } => {
  const d = new Date(isoDate + 'T00:00:00Z');
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayOfWeek = (date.getUTCDay() + 6) % 7;
  const thursday = new Date(date);
  thursday.setUTCDate(date.getUTCDate() - dayOfWeek + 3);
  const isoYear = thursday.getUTCFullYear();
  const firstThursday = new Date(Date.UTC(isoYear, 0, 4));
  const week = 1 + Math.round((thursday.getTime() - firstThursday.getTime()) / (7 * 24 * 60 * 60 * 1000));
  return { label: `Wk${week} (${isoYear})`, isoYear, week };
};

// Return ISO week number and ISO week-year for a date string (YYYY-MM-DD)
const getISOWeekLabel = (isoDate: string): string => getISOWeekInfo(isoDate).label;

// Assign an ISO week to its calendar month using the "Thursday rule":
// the week belongs to whichever month contains that week's Thursday.
const getWeekMonthKey = (isoDate: string): { monthKey: string; monthLabel: string } => {
  const d = new Date(isoDate + 'T00:00:00Z');
  const dow = (d.getUTCDay() + 6) % 7; // Mon=0
  const thu = new Date(d);
  thu.setUTCDate(d.getUTCDate() - dow + 3);
  const y = thu.getUTCFullYear();
  const m = thu.getUTCMonth();
  return {
    monthKey:   `${y}-${String(m + 1).padStart(2, '0')}`,
    monthLabel: thu.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' }),
  };
};

const formatPeriodLabel = (ps: string, pt: PeriodType): string => {
  if (pt === 'week') return getISOWeekLabel(ps);
  const d = new Date(ps + 'T00:00:00Z');
  if (pt === 'month') return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });
  return `Q${Math.floor(d.getUTCMonth() / 3) + 1} ${d.getUTCFullYear()}`;
};

const formatValue = (m: BowlerMetric, v: number | null): string => {
  if (v === null || v === undefined) return 'NA';
  if (m.unit === '%')     return `${v.toFixed(1)}%`;
  if (m.unit === 'Hours') return v.toFixed(1);
  return String(Math.round(v));
};

const formatTarget = (m: BowlerMetric): string => {
  if (m.target === null) return '—';
  const pfx = m.direction === 'up' ? '>= ' : '<= ';
  const sfx = m.unit === '%' ? '%' : m.unit === 'Hours' ? 'h' : '';
  return `${pfx}${m.target}${sfx}`;
};

const formatBaseline = (m: BowlerMetric): string => {
  if (m.baseline === null || m.baseline === undefined) return '—';
  if (typeof m.baseline === 'string') return m.baseline;
  const sfx = m.unit === '%' ? '%' : m.unit === 'Hours' ? 'h' : '';
  return `${m.baseline}${sfx}`;
};

// Metric descriptions / formulas for hover tooltip
const METRIC_DESCRIPTIONS: Record<string, string> = {
  avg_e2e_mttr: 'Average End-to-End MTTR: total resolution time / number of resolved incidents (hours). Derived from materialized view (seconds → hours).',
  p60_e2e_mttr: '60th percentile of End-to-End MTTR (hours).',
  p90_e2e_mttr: '90th percentile of End-to-End MTTR (hours).',
  avg_frt: 'Average First Response Time: time from incident creation to first response (hours).',
  automation_pct: '% Automation = 100 × automated_incidents / total_incidents.',
  automation_mttr: 'Automation MTTR: average MTTR for incidents handled by automation (hours).',
  tori_fux: 'TORI First User Experience: placeholder metric (not yet tracked).',
  new_issue: 'New Issue: placeholder metric for tracking new issue counts (not yet tracked).',
  problem_tickets: 'Problem Tickets: placeholder metric for problem ticket counts (not yet tracked).',
  incs_created: 'INCs Raised: count of incidents created during the period.',
  reopen_pct: '% Reopen Rate = 100 × reopened_incidents / resolved_incidents.',
  update_framework_compliance: '% Update Framework Compliance = 100 × compliant_updates / total_updates.',
  incs_resolved: 'INCs Resolved: count of incidents resolved during the period.',
  inc_resolution_pct: '% INC Resolution = 100 × incs_resolved / incs_created (from materialized view).',
  ftf_pct: 'First Time Fix % = 100 × incidents_fixed_first_time / resolved_incidents.',
  p90_e2e_mttr_manual: '90th percentile E2E MTTR for incidents handled manually (hours). Manually entered.',
  p90_e2e_mttr_automation: '90th percentile E2E MTTR for incidents handled by automation (hours). Manually entered.',
  avg_support_mttr: 'Support MTTR: wall-clock duration of the "Maersk Generic P4 - Resolution" SLA from ServiceNow task_sla table (seconds → hours).',
  avg_mttr_diff: 'MTTR Difference = E2E MTTR − Support MTTR (hours). Measures how much additional time is spent outside the SLA-tracked support window.',
};

const getCellColors = (m: BowlerMetric, v: number | null, effectiveTarget?: number | null): { bg: string; fg: string } | null => {
  const t = effectiveTarget !== undefined ? effectiveTarget : m.target;
  if (v === null || v === undefined || t === null) return null;
  const ok = m.direction === 'down' ? v <= t : v >= t;
  return ok ? { bg: '#dff0d8', fg: '#2e7d32' } : { bg: '#ffcdd2', fg: '#c62828' };
};

// shared sx
const HDR: React.CSSProperties & Record<string, any> = {
  fontWeight: 700, fontSize: '0.8rem', backgroundColor: '#cfe8fc',
  border: '1px solid #b0c4de', textAlign: 'center', whiteSpace: 'nowrap',
};
const TGT: React.CSSProperties & Record<string, any> = {
  fontWeight: 600, fontSize: '0.78rem', backgroundColor: '#dbeafe',
  border: '1px solid #d0d7de', color: '#1e40af', whiteSpace: 'nowrap',
};

// ── Client-side series aggregation (weighted average by incs_resolved) ────────
const aggregateSeries = (keys: string[], lookup: Record<string, SeriesKPI[]>): SeriesKPI[] => {
  const periodMap = new Map<string, SeriesKPI[]>();
  for (const k of keys) {
    for (const row of (lookup[k] ?? [])) {
      const arr = periodMap.get(row.period_start) ?? [];
      arr.push(row);
      periodMap.set(row.period_start, arr);
    }
  }
  return Array.from(periodMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([period_start, rows]) => {
      const totalCreated  = rows.reduce((s, r) => s + (r.incs_created  ?? 0), 0);
      const totalResolved = rows.reduce((s, r) => s + (r.incs_resolved ?? 0), 0);
      const wavg = (key: keyof SeriesKPI): number | null => {
        let num = 0, den = 0;
        for (const r of rows) {
          const v = r[key] as number | null;
          const w = r.incs_resolved ?? 0;
          if (v != null && w > 0) { num += v * w; den += w; }
        }
        return den > 0 ? num / den : null;
      };
      return {
        period_start, incs_created: totalCreated, incs_resolved: totalResolved,
        avg_e2e_mttr:   wavg('avg_e2e_mttr'),   p60_e2e_mttr:  wavg('p60_e2e_mttr'),
        p90_e2e_mttr:   wavg('p90_e2e_mttr'),   avg_frt:        wavg('avg_frt'),
        p90_e2e_mttr_manual:      wavg('p90_e2e_mttr_manual'),
        p90_e2e_mttr_automation:  wavg('p90_e2e_mttr_automation'),
        automation_pct: wavg('automation_pct'),  automation_mttr: wavg('automation_mttr'),
        reopen_pct:     wavg('reopen_pct'),
        update_framework_compliance: wavg('update_framework_compliance'),
        ftf_pct:        wavg('ftf_pct'),
        inc_resolution_pct: totalCreated > 0
          ? parseFloat((100 * totalResolved / totalCreated).toFixed(2)) : null,
        avg_support_mttr: wavg('avg_support_mttr'),
        avg_mttr_diff:    wavg('avg_mttr_diff'),
      };
    });
};

// ── Top Group / Group Category constants ─────────────────────────────────────

const TOP_GROUPS = [
  { value: 'l1',          label: 'Tech Support L1/L1.5' },
  { value: 'servicedesk', label: 'TechSupport Service Desk' },
];

const TOP_GROUP_CATEGORIES: Record<string, string[]> = {
  l1: [
    'Business Enablement Platforms - App',
    'Business Enablement Platforms - Integration',
    'Managed by Maersk',
    'Business Process Ops (Fleet & IoT)',
    'Cargo Wise Service',
    'Fulfilled By Maersk',
    'TbM Landside',
    'TbM Ocean',
  ],
  servicedesk: ['Tech Support Service Desk'],
};

// ── Component ──────────────────────────────────────────────────────────────────

export default function IncidentsBowler() {
  // Get current user email from MSAL accounts (preferred),
  // falling back to a global `window.__USER_EMAIL__` or build-time env.
  const { accounts } = useMsal();
  const msalEmail = accounts && accounts[0] && accounts[0].username ? String(accounts[0].username).trim().toLowerCase() : null;
  const fallbackEmail = (typeof window !== 'undefined' && (window as any).__USER_EMAIL__)
    ? String((window as any).__USER_EMAIL__).trim().toLowerCase()
    : (process.env.REACT_APP_USER_EMAIL || null);
  const currentUserEmail = msalEmail || fallbackEmail;
  const allowedList = (ALLOWED_BOWLER_USERS || []).map(s => String(s).trim().toLowerCase()).filter(Boolean);
  const restrictEnabled = allowedList.length > 0;
  const isAllowed = !restrictEnabled || (currentUserEmail && allowedList.includes(String(currentUserEmail).toLowerCase()));

  // ── Role checks ──────────────────────────────────────────────────────────
  const editList  = (BOWLER_EDIT_USERS  || []).map(s => String(s).trim().toLowerCase()).filter(Boolean);
  const adminList = (BOWLER_ADMIN_USERS || []).map(s => String(s).trim().toLowerCase()).filter(Boolean);
  const isEditUser = editList.length > 0
    ? !!(currentUserEmail && editList.includes(String(currentUserEmail).toLowerCase()))
    : false;
  const isAdmin = adminList.length > 0
    ? !!(currentUserEmail && adminList.includes(String(currentUserEmail).toLowerCase()))
    : false;

  // ── Filter state ─────────────────────────────────────────────────────────
  const [periodType, setPeriodType]               = useState<PeriodType>('week');
  const [timeRange, setTimeRange]                 = useState<TimeRange>('ytd');
  const [topGroup, setTopGroup]                   = useState<string>('l1');
  const [groupCategories, setGroupCategories]     = useState<string[]>([]);    // multi-select
  const [selectedSupportGroups, setSelectedSupportGroups] = useState<string[]>([]); // multi-select
  const [selectedPeriods, setSelectedPeriods]     = useState<string[]>([]);    // period_start column keys
  const [sidebarOpen, setSidebarOpen]             = useState<boolean>(true);
  const [expandedYears, setExpandedYears]         = useState<Set<number>>(new Set([new Date().getFullYear()]));
  const [expandedMonths, setExpandedMonths]       = useState<Set<string>>(() => {
    const today = new Date();
    const dow = (today.getDay() + 6) % 7;
    const thu = new Date(today);
    thu.setDate(today.getDate() - dow + 3);
    return new Set([`${thu.getFullYear()}-${String(thu.getMonth() + 1).padStart(2, '0')}`]);
  });
  const autoSelectRef = useRef(true);
  const bulkCache = useRef<Map<string, BulkData>>(new Map());
  const [bulkData, setBulkData]     = useState<BulkData | null>(null);
  const [loadingBulk, setLoadingBulk] = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [selectedMetricKeys, setSelectedMetricKeys] = useState<Set<string>>(
    () => new Set(DEFAULT_METRIC_KEYS)
  );
  const [metricsOpen, setMetricsOpen]               = useState(false);
  const [overrides, setOverrides]     = useState<Map<string, number | null>>(new Map());
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [auditOpen, setAuditOpen]     = useState(false);
  const [auditLog, setAuditLog]       = useState<AuditEntry[]>([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [savingCell, setSavingCell]   = useState(false);
  const [toast, setToast]             = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);
  const [showOnlyRed, setShowOnlyRed] = useState(true);

  const dateRange = useMemo(() => getDateRange(timeRange), [timeRange]);

  // Monday of the current ISO week — only this week's cells are editable
  const currentWeekStart = useMemo(() => floorToMonday(new Date().toISOString().slice(0, 10)), []);

  // Base edit gate: exactly one group category selected, no support-group drill-down
  const editBaseGate = groupCategories.length === 1 && selectedSupportGroups.length === 0;
  // Edit users: current week only. Admins: any week (past + current).
  const canEditPeriod = useCallback((periodStart: string): boolean => {
    if (!editBaseGate) return false;
    if (isAdmin) return true;
    if (isEditUser) return periodStart === currentWeekStart;
    return false;
  }, [editBaseGate, isAdmin, isEditUser, currentWeekStart]);
  // canEdit: true if the user can edit at least the current week (drives UI like fetchOverrides)
  const canEdit = editBaseGate && (isEditUser || isAdmin);

  // ── Fetch bulk data for the current date range (cached) ───────────────────
  const fetchBulkData = useCallback(async () => {
    // Floor to Monday so Wk1 (period_start = Mon of that week) is never cut off by start filter
    const apiStart = floorToMonday(dateRange.start);
    const cacheKey = `${apiStart}_${dateRange.end}`;
    const hit = bulkCache.current.get(cacheKey);
    if (hit) { setBulkData(hit); return; }
    setLoadingBulk(true);
    setError(null);
    try {
      const p = new URLSearchParams({ start: apiStart, end: dateRange.end });
      const res = await authFetch(
        `${API_BASE_URL}/api/operational-excellence/incidents/bulk-series?${p}`
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      const data: BulkData = j.data;
      bulkCache.current.set(cacheKey, data);
      setBulkData(data);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load data');
      setBulkData(null);
    } finally {
      setLoadingBulk(false);
    }
  }, [dateRange]);

  useEffect(() => {
    autoSelectRef.current = true;
    setSelectedPeriods([]);
    fetchBulkData();
  }, [fetchBulkData]);

  // ── Cascade handlers ──────────────────────────────────────────────────────
  const handleTopGroupChange = (v: string) => {
    setTopGroup(v);
    setGroupCategories([]);
    setSelectedSupportGroups([]);
  };

  const handleGroupCategoriesChange = (val: string[]) => {
    setGroupCategories(val);
    setSelectedSupportGroups([]);
  };

  // ── Derived: support groups available for current category selection ───────
  const availableSupportGroups = useMemo(() => {
    if (!bulkData) return [];
    const cats = groupCategories.length > 0
      ? groupCategories
      : (TOP_GROUP_CATEGORIES[topGroup] ?? []);
    return Object.keys(bulkData.weekly.by_assignment_group)
      .filter(ag => cats.includes(bulkData.group_to_source[ag] ?? ''))
      .sort();
  }, [bulkData, topGroup, groupCategories]);

  // ── Derived: displayed series — no backend call when filters change ─────────────────
  const seriesData = useMemo((): SeriesKPI[] => {
    if (!bulkData) return [];
    const pd = periodType === 'week' ? bulkData.weekly : bulkData.monthly;
    if (selectedSupportGroups.length > 0) {
      return selectedSupportGroups.length === 1
        ? (pd.by_assignment_group[selectedSupportGroups[0]] ?? [])
        : aggregateSeries(selectedSupportGroups, pd.by_assignment_group);
    }
    const cats = groupCategories.length > 0
      ? groupCategories
      : (TOP_GROUP_CATEGORIES[topGroup] ?? []);
    return cats.length === 1
      ? (pd.by_source[cats[0]] ?? [])
      : aggregateSeries(cats, pd.by_source);
  }, [bulkData, periodType, topGroup, groupCategories, selectedSupportGroups]);

  // Auto-select current month's periods after each data load / period-type change
  useEffect(() => {
    if (!autoSelectRef.current || seriesData.length === 0) return;
    autoSelectRef.current = false;
    const today = new Date();
    let curMonthKey: string;
    if (periodType === 'week') {
      const dow = (today.getDay() + 6) % 7;
      const thu = new Date(today);
      thu.setDate(today.getDate() - dow + 3);
      curMonthKey = `${thu.getFullYear()}-${String(thu.getMonth() + 1).padStart(2, '0')}`;
    } else {
      curMonthKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
    }
    const toSelect = seriesData
      .filter(r => {
        if (periodType === 'week') return getWeekMonthKey(r.period_start).monthKey === curMonthKey;
        const d = new Date(r.period_start + 'T00:00:00Z');
        return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}` === curMonthKey;
      })
      .map(r => r.period_start);
    if (toSelect.length > 0) setSelectedPeriods(toSelect);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesData, periodType]);

  // ── Period sidebar: group periods by year → month (ISO Thursday-rule) ──────────────────
  const periodsByYear = useMemo((): Map<number, MonthGroup[]> => {
    const yearMap = new Map<number, Map<string, MonthGroup>>();
    for (const row of seriesData) {
      let isoYear: number;
      let monthKey: string;
      let monthLabel: string;
      let periodLabel: string;
      if (periodType === 'week') {
        const info = getISOWeekInfo(row.period_start);
        const mk   = getWeekMonthKey(row.period_start);
        isoYear    = info.isoYear;
        monthKey   = mk.monthKey;
        monthLabel = mk.monthLabel;
        periodLabel = info.label;
      } else {
        const d = new Date(row.period_start + 'T00:00:00Z');
        isoYear    = d.getUTCFullYear();
        monthKey   = `${isoYear}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
        monthLabel = d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
        periodLabel = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
      }
      if (!yearMap.has(isoYear)) yearMap.set(isoYear, new Map());
      const mMap = yearMap.get(isoYear)!;
      if (!mMap.has(monthKey)) mMap.set(monthKey, { monthKey, monthLabel, periods: [] });
      mMap.get(monthKey)!.periods.push({ key: row.period_start, label: periodLabel });
    }
    const result = new Map<number, MonthGroup[]>();
    yearMap.forEach((mMap, year) => {
      const vals: MonthGroup[] = [];
      mMap.forEach(mg => vals.push(mg));
      result.set(year, vals.sort((a, b) => a.monthKey.localeCompare(b.monthKey)));
    });
    return result;
  }, [seriesData, periodType]);

  // ── Apply period selection filter (client-side column filter) ─────────────────────────
  const displayedSeries = useMemo((): SeriesKPI[] => {
    if (selectedPeriods.length === 0) return seriesData;
    return seriesData.filter(r => selectedPeriods.includes(r.period_start));
  }, [seriesData, selectedPeriods]);

  const toggleMetric = useCallback((key: string) => {
    setSelectedMetricKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const visibleGroups = useMemo((): Map<string, GroupEntry> => {
    const map = new Map<string, GroupEntry>();
    for (const g of GROUP_ORDER) {
      const ms = METRICS.filter(m => m.group === g && selectedMetricKeys.has(String(m.key)));
      if (ms.length > 0) map.set(g, { metrics: ms, rowSpan: ms.length * 2 });
    }
    return map;
  }, [selectedMetricKeys]);

  // ── Sidebar helpers ───────────────────────────────────────────────────────────────────
  const toggleYear = (year: number) => {
    setExpandedYears(prev => {
      const next = new Set(prev);
      if (next.has(year)) next.delete(year); else next.add(year);
      return next;
    });
  };
  const toggleMonth = (monthKey: string) => {
    setExpandedMonths(prev => {
      const next = new Set(prev);
      if (next.has(monthKey)) next.delete(monthKey); else next.add(monthKey);
      return next;
    });
  };
  const monthPeriodKeys = (year: number, monthKey: string): string[] =>
    (periodsByYear.get(year)?.find(mg => mg.monthKey === monthKey)?.periods ?? []).map(p => p.key);
  const yearKeys = (year: number): string[] =>
    (periodsByYear.get(year) ?? []).flatMap(mg => mg.periods.map(p => p.key));
  const monthAllSelected = (year: number, monthKey: string) => {
    const keys = monthPeriodKeys(year, monthKey);
    return keys.length > 0 && keys.every(k => selectedPeriods.includes(k));
  };
  const monthSomeSelected = (year: number, monthKey: string) => {
    const keys = monthPeriodKeys(year, monthKey);
    return keys.some(k => selectedPeriods.includes(k)) && !monthAllSelected(year, monthKey);
  };
  const toggleMonthPeriods = (year: number, monthKey: string) => {
    const keys = monthPeriodKeys(year, monthKey);
    if (monthAllSelected(year, monthKey)) {
      setSelectedPeriods(prev => prev.filter(k => !keys.includes(k)));
    } else {
      setSelectedPeriods(prev => Array.from(new Set([...prev, ...keys])));
    }
  };
  const yearAllSelected = (year: number) => {
    const keys = yearKeys(year);
    return keys.length > 0 && keys.every(k => selectedPeriods.includes(k));
  };
  const yearSomeSelected = (year: number) =>
    yearKeys(year).some(k => selectedPeriods.includes(k)) && !yearAllSelected(year);
  const toggleYearPeriods = (year: number) => {
    const keys = yearKeys(year);
    if (yearAllSelected(year)) {
      setSelectedPeriods(prev => prev.filter(k => !keys.includes(k)));
    } else {
      setSelectedPeriods(prev => Array.from(new Set([...prev, ...keys])));
    }
  };
  const togglePeriod = (key: string) => {
    setSelectedPeriods(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // ── Override helpers ───────────────────────────────────────────────────────
  // Returns the Map key for a given metric + period (or 'target' for targets)
  const ovKey = useCallback((metricKey: string, periodStart: string) =>
    `${metricKey}|${periodStart}`, []);

  // Effective target for a period: carry-forward — find latest stored target override
  // whose period_start <= this period, fall back to hardcoded metric.target if none found.
  const getEffectiveTarget = useCallback((metric: BowlerMetric, periodStart: string): number | null => {
    const prefix = `${String(metric.key)}|target|`;
    let bestDate = '';
    let bestVal: number | null | undefined = undefined;
    for (const [k, v] of Array.from(overrides.entries())) {
      if (k.startsWith(prefix)) {
        const kDate = k.slice(prefix.length);
        if (kDate <= periodStart && kDate > bestDate) {
          bestDate = kDate;
          bestVal = v;
        }
      }
    }
    return bestVal !== undefined ? (bestVal as number | null) : metric.target;
  }, [overrides]);

  // Set of metric keys that have at least one red (off-target) actual cell across displayed periods
  const redMetricKeys = useMemo((): Set<string> => {
    const result = new Set<string>();
    for (const [, { metrics }] of Array.from(visibleGroups.entries())) {
      for (const metric of metrics) {
        for (const p of displayedSeries) {
          const mKey = String(metric.key);
          const rawFromApi = metric.notCalculated ? null : (p[metric.key] as number | null);
          const ovActualKey = `${mKey}|${p.period_start}`;
          const hasActualOv = metric.manual && overrides.has(ovActualKey);
          const effectiveVal = hasActualOv ? (overrides.get(ovActualKey) as number | null) : rawFromApi;
          const effTarget = getEffectiveTarget(metric, p.period_start);
          const isTrackedOrOverridden = !metric.notCalculated || hasActualOv;
          if (isTrackedOrOverridden) {
            const colors = getCellColors(metric, effectiveVal, effTarget);
            if (colors && colors.bg === '#ffcdd2') { result.add(mKey); break; }
          }
        }
      }
    }
    return result;
  }, [visibleGroups, displayedSeries, overrides, getEffectiveTarget]);

  // ── Fetch overrides for the active group category ──────────────────────────
  const fetchOverrides = useCallback(async () => {
    if (!canEdit) { setOverrides(new Map()); return; }
    try {
      const gc = groupCategories[0];
      const res = await authFetch(
        `${API_BASE_URL}/api/operational-excellence/incidents/bowler-overrides?group_category=${encodeURIComponent(gc)}`
      );
      if (!res.ok) return;
      const j = await res.json();
      const map = new Map<string, number | null>();
      for (const ov of (j.data || [])) {
        const k = ov.override_type === 'target'
          ? `${ov.metric_key}|target|${ov.period_start}`   // per-period key, carry-forward in getEffectiveTarget
          : `${ov.metric_key}|${ov.period_start}`;
        map.set(k, ov.value ?? null);
      }
      setOverrides(map);
    } catch (e) {
      console.error('Failed to load overrides', e);
    }
  }, [canEdit, groupCategories]);

  useEffect(() => { fetchOverrides(); }, [fetchOverrides]);

  // ── Save a single cell override ────────────────────────────────────────────
  const saveOverride = useCallback(async (cell: EditingCell) => {
    if (!canEdit || savingCell) return;
    const rawStr = cell.inputVal.trim();
    const numVal = rawStr === '' ? null : parseFloat(rawStr);
    if (rawStr !== '' && isNaN(numVal as number)) { setEditingCell(null); return; }

    // Skip save if value hasn't changed — avoid spurious audit entries
    if (numVal === cell.originalVal) { setEditingCell(null); return; }
    setSavingCell(true);
    try {
      const gc = groupCategories[0];
      const periodForApi = cell.periodStart;   // always the real period date (no sentinel)
      const res = await authFetch(
        `${API_BASE_URL}/api/operational-excellence/incidents/bowler-overrides`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            group_category: gc,
            period_start:   periodForApi,
            metric_key:     cell.metricKey,
            override_type:  cell.type,
            value:          numVal,
            updated_by:     currentUserEmail || 'unknown',
          }),
        }
      );
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      const k = cell.type === 'target'
        ? `${cell.metricKey}|target|${cell.periodStart}`   // per-period key
        : `${cell.metricKey}|${cell.periodStart}`;
      setOverrides(prev => { const n = new Map(prev); n.set(k, numVal ?? null); return n; });
      setToast({ msg: 'Saved successfully', severity: 'success' });
    } catch (e) {
      console.error('Failed to save override', e);
      setToast({ msg: 'Failed to save. Please try again.', severity: 'error' });
    } finally {
      setSavingCell(false);
      setEditingCell(null);
    }
  }, [canEdit, savingCell, groupCategories, currentUserEmail]);

  // ── Fetch audit log ────────────────────────────────────────────────────────
  const fetchAuditLog = useCallback(async () => {
    if (!canEdit) return;
    setLoadingAudit(true);
    try {
      const gc = groupCategories[0];
      const params = new URLSearchParams({ group_category: gc, caller_email: currentUserEmail || '' });
      const res = await authFetch(
        `${API_BASE_URL}/api/operational-excellence/incidents/bowler-overrides/audit?${params}`
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setAuditLog(j.data || []);
    } catch (e) {
      console.error('Failed to fetch audit log', e);
    } finally {
      setLoadingAudit(false);
    }
  }, [canEdit, groupCategories]);

  // ── Export CSV (client-side — mirrors exactly what the user sees on screen) ──
  const handleExportCsv = useCallback(() => {
    try {
      // Determine which metrics to export (respects Metrics filter + Only Red toggle)
      const exportMetrics: BowlerMetric[] = [];
      for (const [, { metrics }] of Array.from(visibleGroups.entries())) {
        for (const m of metrics) {
          if (!showOnlyRed || redMetricKeys.has(String(m.key))) exportMetrics.push(m);
        }
      }

      // Header row: Group | Metric | Row Type | Baseline | Wk14 - 30-03-2026 ...
      const periodCols = displayedSeries.map(p => p.period_start);
      const periodHeaders = periodType === 'week'
        ? periodCols.map(ps => {
            const wkLabel = getISOWeekInfo(ps).label;  // e.g. "Wk14 (2026)"
            const d = new Date(ps + 'T00:00:00Z');
            const dateStr = `${String(d.getUTCDate()).padStart(2,'0')}-${String(d.getUTCMonth()+1).padStart(2,'0')}-${d.getUTCFullYear()}`;
            return `${wkLabel} - ${dateStr}`;
          })
        : periodCols.map(ps => {
            const d = new Date(ps + 'T00:00:00Z');
            return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
          });
      const headerRow = ['Group', 'Metric', 'Row Type', 'Baseline', ...periodHeaders];

      const rows: string[][] = [headerRow];

      for (const metric of exportMetrics) {
        const baseline = metric.baseline !== null ? String(metric.baseline) : '';

        // Target row
        const targetRow: string[] = [
          metric.group,
          metric.label + (metric.unit ? ` (${metric.unit})` : '') + (metric.manual ? ' [Manual]' : ''),
          'Target',
          baseline,
        ];
        for (const p of displayedSeries) {
          if (metric.notCalculated && !metric.manual) {
            targetRow.push('—');
          } else {
            const effTarget = getEffectiveTarget(metric, p.period_start);
            targetRow.push(effTarget !== null ? String(effTarget) : '');
          }
        }
        rows.push(targetRow);

        // Actual row
        const actualRow: string[] = [
          metric.group,
          metric.label + (metric.unit ? ` (${metric.unit})` : '') + (metric.manual ? ' [Manual]' : ''),
          'Actual',
          baseline,
        ];
        for (const p of displayedSeries) {
          const mKey = String(metric.key);
          const rawFromApi = metric.notCalculated ? null : (p[metric.key] as number | null);
          const ovActualKey = `${mKey}|${p.period_start}`;
          const hasActualOv = metric.manual && overrides.has(ovActualKey);
          const effectiveVal = hasActualOv ? (overrides.get(ovActualKey) as number | null) : rawFromApi;
          const isTrackedOrOverridden = !metric.notCalculated || hasActualOv;
          actualRow.push(isTrackedOrOverridden && effectiveVal !== null ? String(effectiveVal) : 'NA');
        }
        rows.push(actualRow);
      }

      // Build CSV string
      const escape = (v: string) => v.includes(',') || v.includes('"') || v.includes('\n')
        ? `"${v.replace(/"/g, '""')}"` : v;
      const csv = rows.map(r => r.map(escape).join(',')).join('\r\n');

      // Trigger download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filterLabel = selectedSupportGroups.length === 1
        ? selectedSupportGroups[0]
        : groupCategories.length === 1 ? groupCategories[0] : topGroup;
      const exportStart = periodCols.length > 0 ? periodCols[0] : dateRange.start;
      const exportEnd   = periodCols.length > 0 ? periodCols[periodCols.length - 1] : dateRange.end;
      a.download = `bowler_${filterLabel}_${exportStart}_${exportEnd}${showOnlyRed ? '_red_only' : ''}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export CSV', err);
      setToast({ msg: 'Failed to export CSV', severity: 'error' });
    }
  }, [visibleGroups, showOnlyRed, redMetricKeys, displayedSeries, getEffectiveTarget, overrides,
      selectedSupportGroups, groupCategories, topGroup, dateRange]);

  // available group categories for the selected top group
  const availableGroupCategories = TOP_GROUP_CATEGORIES[topGroup] ?? [];

  // Access control: if allowlist is enabled and current user not present, show an error
  if (!isAllowed) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">You do not have permission to view the Incidents Bowler.</Alert>
        <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
          To grant access, add the user's email to <strong>src/config/allowed_bowler_users.ts</strong> or set the global
          <code>window.__USER_EMAIL__</code> and rebuild the UI.
        </Typography>
      </Box>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

      {/* ── Left sidebar: Filters + Year / Period selector ────────────── */}
      <Box sx={{
        width: sidebarOpen ? 230 : 32,
        minWidth: sidebarOpen ? 230 : 32,
        transition: 'width 0.2s, min-width 0.2s',
        borderRight: '1px solid #e0e0e0',
        display: 'flex', flexDirection: 'column',
        backgroundColor: '#f8faff',
        overflow: 'hidden',
      }}>
        {/* Sidebar header: title + collapse toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: sidebarOpen ? 'space-between' : 'center',
          px: sidebarOpen ? 1 : 0, py: 0.5, borderBottom: '1px solid #e0e0e0', minHeight: 36, flexShrink: 0 }}>
          {sidebarOpen && (
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1976d2' }}>
              Incidents Bowler
            </Typography>
          )}
          <Tooltip title={sidebarOpen ? 'Collapse panel' : 'Expand panel'}>
            <IconButton size="small" onClick={() => setSidebarOpen(v => !v)} sx={{ p: 0.25 }}>
              {sidebarOpen ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        {sidebarOpen && (
          <Box sx={{ overflowY: 'auto', flex: 1, pb: 1 }}>

            {/* ── Filters ─────────────────────────────────────────── */}
            <Box sx={{ px: 1, pt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Period Type</InputLabel>
                <Select value={periodType} label="Period Type" onChange={e => { setPeriodType(e.target.value as PeriodType); setSelectedPeriods([]); autoSelectRef.current = true; }}>
                  <MenuItem value="week">Weekly</MenuItem>
                  <MenuItem value="month">Monthly</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel>Time Range</InputLabel>
                <Select value={timeRange} label="Time Range" onChange={e => setTimeRange(e.target.value as TimeRange)}>
                  <MenuItem value="4w">Last 4 weeks</MenuItem>
                  <MenuItem value="13w">Last 13 weeks</MenuItem>
                  <MenuItem value="26w">Last 26 weeks</MenuItem>
                  <MenuItem value="ytd">Year to Date</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel>Top Group</InputLabel>
                <Select value={topGroup} label="Top Group" onChange={e => handleTopGroupChange(e.target.value)}>
                  {TOP_GROUPS.map(g => (
                    <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel>Group Category</InputLabel>
                <Select
                  multiple
                  value={groupCategories}
                  label="Group Category"
                  onChange={e => handleGroupCategoriesChange(e.target.value as string[])}
                  renderValue={sel =>
                    (sel as string[]).length === 0
                      ? <em style={{ color: '#888' }}>All</em>
                      : (sel as string[]).join(', ')
                  }
                >
                  {availableGroupCategories.map(gc => (
                    <MenuItem key={gc} value={gc} dense>
                      <Checkbox checked={groupCategories.includes(gc)} size="small" sx={{ py: 0 }} />
                      <ListItemText primary={gc} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth disabled={availableSupportGroups.length === 0}>
                <InputLabel>Support Group</InputLabel>
                <Select
                  multiple
                  value={selectedSupportGroups}
                  label="Support Group"
                  onChange={e => setSelectedSupportGroups(e.target.value as string[])}
                  renderValue={sel =>
                    (sel as string[]).length === 0
                      ? <em style={{ color: '#888' }}>All</em>
                      : (sel as string[]).join(', ')
                  }
                >
                  {availableSupportGroups.map(sg => (
                    <MenuItem key={sg} value={sg} dense>
                      <Checkbox checked={selectedSupportGroups.includes(sg)} size="small" sx={{ py: 0 }} />
                      <ListItemText primary={sg} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* ── Metric selector ─────────────────────────────── */}
              <Box sx={{ border: '1px solid #d5e6f5', borderRadius: 1, overflow: 'hidden' }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', px: 1, py: 0.5,
                    backgroundColor: '#e3f0ff', '&:hover': { backgroundColor: '#d0e8ff' } }}
                  onClick={() => setMetricsOpen(v => !v)}
                >
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, flex: 1 }}>
                    {'Metrics '}
                    <Typography component="span" sx={{ color: '#888', fontWeight: 400, fontSize: '0.72rem' }}>
                      ({selectedMetricKeys.size}/{METRICS.length})
                    </Typography>
                  </Typography>
                  {metricsOpen ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
                </Box>
                {metricsOpen && (
                  <Box sx={{ backgroundColor: '#f8faff' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 1, py: 0.25 }}>
                      <Typography
                        sx={{ fontSize: '0.68rem', color: '#1976d2', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        onClick={() => setSelectedMetricKeys(new Set(METRICS.map(m => String(m.key))))}
                      >All</Typography>
                      <Typography
                        sx={{ fontSize: '0.68rem', color: '#999', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        onClick={() => setSelectedMetricKeys(new Set())}
                      >Clear</Typography>
                    </Box>
                    {GROUP_ORDER.map(g => {
                      const gMs = METRICS.filter(m => m.group === g);
                      return (
                        <Box key={g}>
                          <Typography sx={{ fontSize: '0.67rem', fontWeight: 700, color: '#555', px: 1, pt: 0.5,
                            textTransform: 'uppercase', letterSpacing: '0.04em' }}>{g}</Typography>
                          {gMs.map(m => (
                            <Box key={String(m.key)} sx={{ display: 'flex', alignItems: 'center', pl: 1, pr: 0.5,
                              cursor: 'pointer', '&:hover': { backgroundColor: '#eef6ff' } }}
                              onClick={() => toggleMetric(String(m.key))}
                            >
                              <Checkbox
                                size="small" sx={{ p: 0.25 }}
                                checked={selectedMetricKeys.has(String(m.key))}
                                onChange={() => toggleMetric(String(m.key))}
                                onClick={e => e.stopPropagation()}
                              />
                              <Typography sx={{ fontSize: '0.72rem', flex: 1 }}>{m.label}</Typography>
                              {m.manual && (
                                <Box component="span" sx={{ fontSize: '0.58rem', fontWeight: 700, px: 0.4, py: 0,
                                  borderRadius: 0.5, backgroundColor: '#fff3e0', color: '#e65100',
                                  border: '1px solid #ffcc80', ml: 0.5, whiteSpace: 'nowrap',
                                  lineHeight: 1.6, display: 'inline-block' }}>M</Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Tooltip title="Refresh data (clears cache)">
                  <IconButton size="small" onClick={() => {
                    const apiStart = floorToMonday(dateRange.start);
                    bulkCache.current.delete(`${apiStart}_${dateRange.end}`);
                    fetchBulkData();
                  }}>
                    <Refresh />
                  </IconButton>
                </Tooltip>
                <Button size="small" variant="outlined" fullWidth onClick={handleExportCsv}>
                  Export CSV
                </Button>
              </Box>
              <Button
                size="small" variant="text" fullWidth startIcon={<History fontSize="small" />}
                sx={{ fontSize: '0.75rem', color: '#555', mt: 0.5 }}
                onClick={() => { setAuditOpen(true); fetchAuditLog(); }}
              >
                View Change History
              </Button>
            </Box>

            <Divider sx={{ my: 1 }} />

            {/* ── Year / Period selector ──────────────────────────── */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, pb: 0.25 }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1976d2', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Year / Period
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Typography
                  sx={{ fontSize: '0.68rem', color: '#1976d2', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => setSelectedPeriods(seriesData.map(r => r.period_start))}
                >All</Typography>
                <Typography
                  sx={{ fontSize: '0.68rem', color: '#999', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => setSelectedPeriods([])}
                >Clear</Typography>
              </Box>
            </Box>

            {Array.from(periodsByYear.entries())
              .sort(([a], [b]) => a - b)
              .map(([year, monthGroups]) => (
                <Box key={year}>
                  {/* Year row */}
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', px: 0.5,
                      backgroundColor: '#e3f0ff', borderTop: '1px solid #cce0f5', '&:hover': { backgroundColor: '#d0e8ff' } }}
                    onClick={() => toggleYear(year)}
                  >
                    <Checkbox
                      size="small" sx={{ p: 0.25 }}
                      checked={yearAllSelected(year)}
                      indeterminate={yearSomeSelected(year)}
                      onChange={e => { e.stopPropagation(); toggleYearPeriods(year); }}
                      onClick={e => e.stopPropagation()}
                    />
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, flex: 1 }}>{year}</Typography>
                    {expandedYears.has(year) ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
                  </Box>

                  {/* Month rows */}
                  {expandedYears.has(year) && monthGroups.map(mg => (
                    <Box key={mg.monthKey}>
                      {/* Month header row */}
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', pl: 0.5, pr: 0.5,
                          backgroundColor: '#edf4ff', borderTop: '1px solid #d5e6f5',
                          '&:hover': { backgroundColor: '#e0eeff' } }}
                        onClick={() => toggleMonth(mg.monthKey)}
                      >
                        <Checkbox
                          size="small" sx={{ p: 0.25 }}
                          checked={monthAllSelected(year, mg.monthKey)}
                          indeterminate={monthSomeSelected(year, mg.monthKey)}
                          onChange={e => { e.stopPropagation(); toggleMonthPeriods(year, mg.monthKey); }}
                          onClick={e => e.stopPropagation()}
                        />
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, flex: 1 }}>{mg.monthLabel}</Typography>
                        {expandedMonths.has(mg.monthKey)
                          ? <ChevronLeft sx={{ fontSize: '0.9rem' }} />
                          : <ChevronRight sx={{ fontSize: '0.9rem' }} />}
                      </Box>

                      {/* Week rows */}
                      {expandedMonths.has(mg.monthKey) && mg.periods.map(p => (
                        <Box
                          key={p.key}
                          sx={{ display: 'flex', alignItems: 'center', pl: 1.5, pr: 1, cursor: 'pointer',
                            backgroundColor: selectedPeriods.includes(p.key) ? '#dceeff' : 'transparent',
                            '&:hover': { backgroundColor: '#eef6ff' },
                          }}
                          onClick={() => togglePeriod(p.key)}
                        >
                          <Checkbox
                            size="small" sx={{ p: 0.25 }}
                            checked={selectedPeriods.includes(p.key)}
                            onChange={() => togglePeriod(p.key)}
                            onClick={e => e.stopPropagation()}
                          />
                          <Typography sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>{p.label}</Typography>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
              ))
            }
          </Box>
        )}
      </Box>

      {/* ── Main content ───────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, minWidth: 0, p: 2, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

        {/* Bowler table */}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loadingBulk ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : displayedSeries.length === 0 ? (
          <Alert severity="info">{selectedPeriods.length > 0 ? 'No data for the selected periods.' : 'No data for the selected filters and time range.'}</Alert>
        ) : (
          <>
            {/* ── Red filter toggle ── */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box sx={{ display: 'flex', borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
                <Box
                  onClick={() => setShowOnlyRed(false)}
                  sx={{
                    px: 1.5, py: 0.5, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                    backgroundColor: !showOnlyRed ? '#1976d2' : '#f5f5f5',
                    color: !showOnlyRed ? '#fff' : '#555',
                    '&:hover': { backgroundColor: !showOnlyRed ? '#1565c0' : '#ebebeb' },
                    transition: 'background-color 0.15s',
                  }}
                >All</Box>
                <Box
                  onClick={() => setShowOnlyRed(true)}
                  sx={{
                    px: 1.5, py: 0.5, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    backgroundColor: showOnlyRed ? '#c62828' : '#f5f5f5',
                    color: showOnlyRed ? '#fff' : '#555',
                    borderLeft: '1px solid #e0e0e0',
                    '&:hover': { backgroundColor: showOnlyRed ? '#b71c1c' : '#ebebeb' },
                    transition: 'background-color 0.15s',
                  }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: showOnlyRed ? '#fff' : '#c62828', flexShrink: 0 }} />
                  Only Red
                  {redMetricKeys.size > 0 && (
                    <Box component="span" sx={{ ml: 0.5, px: 0.6, py: 0, fontSize: '0.68rem', fontWeight: 700,
                      borderRadius: 1, backgroundColor: showOnlyRed ? 'rgba(255,255,255,0.25)' : '#ffcdd2', color: showOnlyRed ? '#fff' : '#c62828' }}>
                      {redMetricKeys.size}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflowX: 'auto' }}>
              <Table size="small" sx={{ borderCollapse: 'collapse' }}>

                {/* ── Column headers ── */}
                <TableHead>
                  <TableRow>
                    <TableCell colSpan={4} sx={{ ...HDR, textAlign: 'left', pl: 2 }}>Metric</TableCell>
                    {displayedSeries.map(p => (
                      <TableCell key={p.period_start} sx={{ ...HDR, minWidth: 90 }}>
                        {formatPeriodLabel(p.period_start, periodType)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {Array.from(visibleGroups.entries()).flatMap(([groupName, { metrics }], gIdx) => {
                    const filteredMetrics = showOnlyRed
                      ? metrics.filter(m => redMetricKeys.has(String(m.key)))
                      : metrics;
                    if (filteredMetrics.length === 0) return [];
                    const grpBg = gIdx % 2 === 0 ? '#cfe8fc' : '#dbeafe';
                    const rowBg = '#f0f7ff';
                    const rowSpan = filteredMetrics.length * 2;
                    return filteredMetrics.map((metric, mIdx) => {
                      return (
                        <React.Fragment key={metric.key}>

                          {/* TARGET row */}
                          <TableRow sx={{ height: 36 }}>
                            {mIdx === 0 && (
                              <TableCell
                                rowSpan={rowSpan}
                                sx={{
                                  fontWeight: 700, fontSize: '0.75rem',
                                  backgroundColor: grpBg,
                                  border: '1px solid #d0d7de',
                                  verticalAlign: 'middle', textAlign: 'center',
                                  width: 90, minWidth: 90, maxWidth: 110,
                                  lineHeight: 1.3, px: 0.5, py: 0.5,
                                  wordBreak: 'break-word',
                                }}
                              >
                                {groupName}
                              </TableCell>
                            )}
                            <TableCell
                              rowSpan={2}
                              sx={{ fontWeight: 600, fontSize: '0.82rem',
                                backgroundColor: metric.manual ? '#fffbf0' : rowBg,
                                border: '1px solid #d0d7de',
                                borderLeft: metric.manual ? '4px solid #ff9800' : '4px solid #90caf9',
                                minWidth: 130, verticalAlign: 'middle' }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.82rem', lineHeight: 1.2 }}>{metric.label}</Typography>
                                    {metric.manual && (
                                      <Box component="span" sx={{ fontSize: '0.58rem', fontWeight: 700, px: 0.5, py: 0,
                                        borderRadius: 0.5, backgroundColor: '#fff3e0', color: '#e65100',
                                        border: '1px solid #ffcc80', lineHeight: 1.6, display: 'inline-block',
                                        verticalAlign: 'middle', whiteSpace: 'nowrap' }}>Manual</Box>
                                    )}
                                  </Box>
                                  <Typography variant="caption" sx={{ color: '#888', fontSize: '0.70rem' }}>
                                    {metric.unit} · {metric.direction === 'up' ? '↑ Up' : '↓ Down'}
                                  </Typography>
                                </Box>
                                <Tooltip title={<div style={{ maxWidth: 360 }}><strong>Formula / Logic</strong><div style={{ marginTop: 6 }}>{METRIC_DESCRIPTIONS[String(metric.key)] || 'No description available.'}</div></div>}>
                                  <IconButton size="small" aria-label={`info-${String(metric.key)}`}>
                                    <InfoOutlined fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                            <TableCell
                              rowSpan={2}
                              sx={{ textAlign: 'center', fontSize: '0.8rem', fontWeight: 600, backgroundColor: rowBg, border: '1px solid #d0d7de', verticalAlign: 'middle', minWidth: 70, color: '#555' }}
                            >
                              {formatBaseline(metric)}
                            </TableCell>
                            <TableCell sx={(metric.notCalculated && !metric.manual)
                              ? { fontWeight: 600, fontSize: '0.78rem', backgroundColor: '#f0f0f0', border: '1px solid #d0d7de', color: '#9e9e9e', whiteSpace: 'nowrap' }
                              : metric.manual
                                ? { fontWeight: 600, fontSize: '0.78rem', backgroundColor: '#fff3e0', border: '1px solid #ffcc80', color: '#e65100', whiteSpace: 'nowrap' }
                                : { ...TGT }
                            }>Target</TableCell>
                            {displayedSeries.map(p => {
                              // Truly-not-tracked (notCalculated but NOT manual) → render — and skip editing
                              if (metric.notCalculated && !metric.manual) {
                                return <TableCell key={`tgt-${metric.key}-${p.period_start}`} sx={{ textAlign: 'center', fontSize: '0.8rem', fontWeight: 600, backgroundColor: '#f0f0f0', border: '1px solid #d0d7de', color: '#9e9e9e' }}>&#8212;</TableCell>;
                              }
                              const mKey        = String(metric.key);
                              const effTarget   = getEffectiveTarget(metric, p.period_start);
                              const isCurrentWk = p.period_start === currentWeekStart;
                              // isOverridden: this exact period has a target override stored
                              const isOverridden = overrides.has(`${mKey}|target|${p.period_start}`);
                              // isCarryForward: a past period has an override that flows into this period
                              const isCarryForward = !isOverridden && effTarget !== metric.target;
                              const isEditingThis = Boolean(
                                editingCell && editingCell.metricKey === mKey &&
                                editingCell.type === 'target' && editingCell.periodStart === p.period_start
                              );
                              const targetLabel = effTarget !== null
                                ? `${metric.direction === 'up' ? '>= ' : '<= '}${effTarget}${metric.unit === '%' ? '%' : metric.unit === 'Hours' ? 'h' : ''}`
                                : '\u2014';
                              return (
                                <TableCell
                                  key={`tgt-${metric.key}-${p.period_start}`}
                                  sx={{
                                    textAlign: 'center', fontSize: '0.8rem',
                                    ...(metric.notCalculated ? {} : metric.manual ? { fontWeight: 600, fontSize: '0.78rem', backgroundColor: '#fff3e0', border: '1px solid #ffcc80', color: '#e65100' } : TGT), p: '2px 4px',
                                    ...(canEditPeriod(p.period_start) ? { cursor: 'text', '&:hover': { outline: metric.manual ? '2px dashed #ff9800' : '2px dashed #90caf9', outlineOffset: '-2px' } } : {}),
                                    ...(isOverridden ? { fontStyle: 'italic' } : {}),
                                    ...(isCarryForward ? { opacity: 0.75 } : {}),
                                  }}
                                  onClick={canEditPeriod(p.period_start) && !isEditingThis
                                    ? () => setEditingCell({ metricKey: mKey, periodStart: p.period_start, type: 'target', inputVal: effTarget !== null ? String(effTarget) : '', originalVal: effTarget })
                                    : undefined
                                  }
                                >
                                  {isEditingThis ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                      <input
                                        autoFocus
                                        value={editingCell!.inputVal}
                                        disabled={savingCell}
                                        onChange={e => setEditingCell(prev => prev ? { ...prev, inputVal: e.target.value } : null)}
                                        onBlur={() => saveOverride(editingCell!)}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); saveOverride(editingCell!); } if (e.key === 'Escape') setEditingCell(null); }}
                                        style={{ width: 52, padding: '2px 4px', fontSize: '0.8rem', textAlign: 'center', border: '1px solid #1976d2', borderRadius: 3, background: 'white', color: '#333', opacity: savingCell ? 0.5 : 1 }}
                                      />
                                      {savingCell && <CircularProgress size={10} thickness={5} sx={{ color: '#1976d2' }} />}
                                    </Box>
                                  ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3 }}>
                                      <span>{targetLabel}</span>
                                      {canEditPeriod(p.period_start) && <EditOutlined sx={{ fontSize: '0.65rem', opacity: 0, color: metric.manual ? '#ff9800' : '#1976d2', '.MuiTableCell-root:hover &': { opacity: 0.5 } }} />}
                                      {(isOverridden || isCarryForward) && (
                                        <Tooltip title={isOverridden ? 'Target overridden for this week' : 'Target carried forward from a past override'}>
                                          <Box component="span" sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: isCarryForward ? '#78909c' : '#1976d2', display: 'inline-block', ml: 0.3, flexShrink: 0 }} />
                                        </Tooltip>
                                      )}
                                    </Box>
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>

                          {/* ACTUAL row */}
                          <TableRow sx={{ height: 36 }}>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', backgroundColor: rowBg, border: '1px solid #d0d7de', whiteSpace: 'nowrap' }}>
                              Actual
                            </TableCell>
                            {displayedSeries.map(p => {
                              const mKey        = String(metric.key);
                              const isCurrentWk = p.period_start === currentWeekStart;
                              const rawFromApi  = metric.notCalculated ? null : (p[metric.key] as number | null);
                              // Apply manual actual override if present
                              const ovActualKey = `${mKey}|${p.period_start}`;
                              const hasActualOv = metric.manual && overrides.has(ovActualKey);
                              const effectiveVal = hasActualOv ? (overrides.get(ovActualKey) as number | null) : rawFromApi;
                              const effTarget   = getEffectiveTarget(metric, p.period_start);
                              // For notCalculated metrics: only go grey if no manual override has been entered yet
                              const isTrackedOrOverridden = !metric.notCalculated || hasActualOv;
                              const computedColors = getCellColors(metric, effectiveVal, effTarget);
                              const colors = !isTrackedOrOverridden
                                ? (metric.manual ? { bg: '#fffbf0', fg: '#e65100' } : { bg: '#f0f0f0', fg: '#9e9e9e' })
                                : (computedColors ?? (metric.manual ? { bg: '#fffbf0', fg: '#e65100' } : { bg: '#f0f7ff', fg: '#555' }));
                              const display = isTrackedOrOverridden ? formatValue(metric, effectiveVal) : 'NA';

                              // Editable: manual metric, edit user (current week only) or admin (any week)
                              const isEditable = metric.manual && canEditPeriod(p.period_start);
                              const isEditingThis = Boolean(
                                editingCell && editingCell.metricKey === mKey &&
                                editingCell.periodStart === p.period_start && editingCell.type === 'actual'
                              );

                              if (isEditable) {
                                return (
                                  <TableCell
                                    key={`act-${metric.key}-${p.period_start}`}
                                    sx={{
                                      textAlign: 'center', fontWeight: 700, fontSize: '0.85rem',
                                      border: '1px solid #d0d7de', borderLeft: '2px solid #ff9800',
                                      backgroundColor: colors?.bg ?? '#fffbf0',
                                      color: colors?.fg ?? '#e65100',
                                      cursor: 'text', p: '2px 4px',
                                      '&:hover': { borderLeft: '2px dashed #ff9800', outline: '1px dashed #ff9800', outlineOffset: '-2px' },
                                    }}
                                    onClick={() => !isEditingThis && setEditingCell({
                                      metricKey: mKey, periodStart: p.period_start, type: 'actual',
                                      inputVal: effectiveVal !== null ? String(effectiveVal) : '',
                                      originalVal: effectiveVal,
                                    })}
                                  >
                                    {isEditingThis ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                        <input
                                          autoFocus
                                          value={editingCell!.inputVal}
                                          disabled={savingCell}
                                          onChange={e => setEditingCell(prev => prev ? { ...prev, inputVal: e.target.value } : null)}
                                          onBlur={() => saveOverride(editingCell!)}
                                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); saveOverride(editingCell!); } if (e.key === 'Escape') setEditingCell(null); }}
                                          style={{ width: 52, padding: '2px 4px', fontSize: '0.8rem', textAlign: 'center', border: '1px solid #ff9800', borderRadius: 3, background: 'white', color: '#333', opacity: savingCell ? 0.5 : 1 }}
                                        />
                                        {savingCell && <CircularProgress size={10} thickness={5} sx={{ color: '#ff9800' }} />}
                                      </Box>
                                    ) : (
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3 }}>
                                        <span>{effectiveVal !== null ? display : <em style={{ fontSize: '0.7rem', color: '#bbb' }}>enter</em>}</span>
                                        <EditOutlined sx={{ fontSize: '0.65rem', opacity: 0.45, color: '#ff9800' }} />
                                      </Box>
                                    )}
                                  </TableCell>
                                );
                              }

                              return (
                                <TableCell
                                  key={`act-${metric.key}-${p.period_start}`}
                                  sx={{ textAlign: 'center', fontWeight: 700, fontSize: '0.85rem', border: '1px solid #d0d7de', backgroundColor: colors?.bg, color: colors?.fg }}
                                >
                                  {display}
                                </TableCell>
                              );
                            })}
                          </TableRow>

                        </React.Fragment>
                      );
                    });
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Legend */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2, flexWrap: 'wrap' }}>
              {[
                ['#dbeafe', '#90caf9', 'Target (Auto)'],
                ['#fff3e0', '#ffcc80', 'Target (Manual)'],
                ['#dff0d8', '#a5d6a7', 'On Target'],
                ['#ffcdd2', '#ef9a9a', 'Off Target'],
                ['#f0f0f0', '#bdbdbd', 'Not Yet Tracked'],
                ['#fffbf0', '#ff9800', 'Manual Metric'],
                ['#f0f7ff', '#90caf9', 'Auto-Calculated'],
              ].map(([bg, bd, lbl]) => (
                <Box key={lbl} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 14, height: 14, bgcolor: bg, border: `1px solid ${bd}` }} />
                  <Typography variant="caption">{lbl}</Typography>
                </Box>
              ))}
              {selectedPeriods.length > 0 && (
                <Typography
                  variant="caption"
                  sx={{ color: '#1976d2', cursor: 'pointer', ml: 'auto', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => setSelectedPeriods([])}
                >
                  Clear period filter ({selectedPeriods.length} selected)
                </Typography>
              )}
              <Typography variant="caption" sx={{ color: '#999' }}>Time metrics in hours · Baseline = Q4 2025</Typography>
            </Box>
          </>
        )}
      </Box>
    </Box>

    {/* ── Audit Trail Dialog ─────────────────────────────────────────────── */}
    <Dialog open={auditOpen} onClose={() => setAuditOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <History fontSize="small" />
        Change History
        {groupCategories.length > 0 && (
          <Typography variant="caption" sx={{ ml: 1, color: '#888' }}>
            {groupCategories[0]}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {loadingAudit ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : auditLog.length === 0 ? (
          <Box sx={{ p: 3 }}><Typography variant="body2" color="text.secondary">No changes recorded yet.</Typography></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f0f7ff' }}>
                  {['When', 'Who', 'Metric', 'Type', 'Period', 'Old', 'New'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLog.map(a => (
                  <TableRow key={a.id} hover>
                    <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                      {new Date(a.updated_at).toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{a.updated_by}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{a.metric_key}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      <Box component="span" sx={{
                        px: 0.5, py: 0, borderRadius: 0.5, fontSize: '0.68rem', fontWeight: 700,
                        backgroundColor: a.override_type === 'target' ? '#dbeafe' : '#fff3e0',
                        color: a.override_type === 'target' ? '#1e40af' : '#e65100',
                      }}>
                        {a.override_type}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                      {a.period_start === '1970-01-01' ? 'global' : a.period_start}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', color: '#c62828' }}>
                      {a.old_value !== null ? a.old_value : <em style={{ color: '#aaa' }}>—</em>}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', color: '#2e7d32', fontWeight: 700 }}>
                      {a.new_value !== null ? a.new_value : <em style={{ color: '#aaa' }}>—</em>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={() => setAuditOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>

    <Snackbar
      open={Boolean(toast)}
      autoHideDuration={2500}
      onClose={() => setToast(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        px: 2, py: 1, borderRadius: 2, fontSize: '0.85rem', fontWeight: 500,
        backgroundColor: toast?.severity === 'success' ? '#e8f5e9' : '#ffebee',
        color: toast?.severity === 'success' ? '#2e7d32' : '#c62828',
        border: `1px solid ${toast?.severity === 'success' ? '#a5d6a7' : '#ef9a9a'}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
      }}>
        {toast?.severity === 'success'
          ? <CheckCircleOutline fontSize="small" />
          : <span style={{ fontSize: '1rem' }}>⚠</span>}
        {toast?.msg}
      </Box>
    </Snackbar>
    </>
  );
}
